<template>
  <v-dialog :value="dialog" @input="$emit('update:dialog', $event)" fullscreen hide-overlay transition="dialog-bottom-transition">
    <v-card dark tile class="exercise-dialog-bg">
      <v-toolbar flat dark color="transparent" class="pt-2">
        <v-btn icon dark @click="$emit('update:dialog', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title class="font-weight-bold">{{ dialogTitle }}</v-toolbar-title>
      </v-toolbar>

      <v-card-text class="pa-4 pt-0">
        <div v-if="mode === 'swap' && exerciseToSwap" class="context-card mb-4">
          <div class="context-label">Tausche aus:</div>
          <v-list-item class="px-0">
            <v-list-item-avatar tile size="50" class="rounded-lg">
              <v-img :src="getImageForExercise(exerciseToSwap)"></v-img>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold white--text">{{ exerciseToSwap.title }}</v-list-item-title>
              <v-list-item-subtitle>
                <v-chip
                  v-for="tag in getMuscleGroups(exerciseToSwap)"
                  :key="tag"
                  x-small
                  dark
                  color="rgba(255, 255, 255, 0.2)"
                  class="muscle-chip mr-1">
                  {{ tag }}
                </v-chip>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </div>

        <div class="filter-section mb-3 d-flex align-center">
          <v-text-field
            v-model="exerciseSearchInput"
            :label="searchLabel"
            filled
            rounded
            dense
            clearable
            solo
            flat
            hide-details
            background-color="rgba(255, 255, 255, 0.1)"
            prepend-inner-icon="mdi-magnify"
            class="mr-2"></v-text-field>
          <v-menu offset-y :close-on-content-click="false" max-height="400">
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="grey darken-2" v-bind="attrs" v-on="on" class="white--text">
                <v-icon left>mdi-filter-variant</v-icon> Filter
                <v-badge v-if="selectedTags.length > 0" color="primary" :content="selectedTags.length" inline class="ml-2"></v-badge>
              </v-btn>
            </template>
            <v-card class="glass-card pa-3" dark width="350">
              <v-text-field v-model="tagSearch" label="Filter suchen..." dense outlined hide-details class="mb-3"></v-text-field>
              <v-chip-group dark v-model="selectedTags" column multiple active-class="primary--text">
                <v-chip v-for="tag in filteredTags" :value="tag" :key="tag" filter outlined>{{ tag }}</v-chip>
              </v-chip-group>
            </v-card>
          </v-menu>
        </div>

        <div class="list-container" :style="{ height: listContainerHeight }">
          <v-row no-gutters dense>
            <v-col cols="12" class="pa-1" v-for="(item, index) in filteredExercises" :key="item._id">
              <v-lazy transition="fade-transition" :options="{ threshold: 0.5 }">
                <v-card
                  @click="selectExercise(item)"
                  class="glass-card exercise-card-source elevation-4 animated-item"
                  :style="{ '--animation-delay': `${index * 0.05}s` }">
                  <div class="d-flex align-center pa-2">
                    <v-img
                      :src="getImageForExercise(item)"
                      :lazy-src="getPlaceholderImage()"
                      class="rounded-lg"
                      style="max-width: 76px; height: 76px"
                      position="top center" />
                    <div class="exercise-content">
                      <div class="font-weight-bold white--text text-subtitle-1">{{ item.title }}</div>
                      <div class="grey--text text--lighten-1 text-caption">{{ item.deviceShortForm || "Allgemein" }}</div>
                    </div>
                    <div class="d-flex flex-row align-center pr-2">
                      <v-icon color="grey lighten-1" x-large>mdi-chevron-right</v-icon>
                    </div>
                  </div>
                </v-card>
              </v-lazy>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex"
import api from "@/api" // Import der API-Funktionen

export default {
  name: "ExerciseSelectionDialog",
  props: {
    dialog: { type: Boolean, required: true },
    mode: { type: String, default: "swap", validator: (val) => ["swap", "add"].includes(val) },
    insertionIndex: { type: Number, default: 0 },
    exerciseToSwap: { type: Object, default: null },
  },
  data() {
    return {
      exerciseSearch: "",
      exerciseSearchInput: "",
      tagSearch: "",
      selectedTags: [],
      tags: [],
      searchDebounce: null,
    }
  },
  computed: {
    ...mapGetters("deviceStore", ["exerciseList"]),

    dialogTitle() {
      return this.mode === "swap" ? "Übung austauschen" : "Übung hinzufügen"
    },

    searchLabel() {
      return this.mode === "swap" ? "Finde passenden Ersatz..." : "Suche eine Übung..."
    },

    listContainerHeight() {
      const toolbarHeight = 64
      const filterSectionHeight = 60
      const contextCardHeight = this.mode === "swap" && this.exerciseToSwap ? 86 : 0
      const bottomPadding = 16
      const totalOffset = toolbarHeight + filterSectionHeight + contextCardHeight + bottomPadding
      return `calc(100vh - ${totalOffset}px)`
    },

    filteredTags() {
      if (!this.tagSearch) return this.tags
      return this.tags.filter((tag) => tag.toLowerCase().includes(this.tagSearch.toLowerCase()))
    },

    filteredExercises() {
      if (!this.exerciseList) return []

      const searchLower = this.exerciseSearch ? this.exerciseSearch.toLowerCase() : ""

      const baseList =
        this.mode === "swap" && this.exerciseToSwap ? this.exerciseList.filter((ex) => ex._id !== this.exerciseToSwap._id) : this.exerciseList

      return baseList.filter((exercise) => {
        if (exercise.deleted || exercise.deviceCategory === "(ALT) MedX Kraftgeräte") {
          return false
        }

        const matchesTags = this.selectedTags.length === 0 || (exercise.tags && exercise.tags.some((tag) => this.selectedTags.includes(tag.name)))
        if (!matchesTags) return false

        if (searchLower === "") return true

        return (
          exercise.title.toLowerCase().includes(searchLower) ||
          (exercise.deviceShortForm && exercise.deviceShortForm.toLowerCase().includes(searchLower))
        )
      })
    },
  },
  watch: {
    exerciseSearchInput(newValue) {
      clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => {
        this.exerciseSearch = newValue
      }, 300)
    },
  },
  async created() {
    try {
      const fetchedTags = await api.fetchExerciseTags()
      this.tags = [...new Set(fetchedTags.map((tag) => tag.name || tag).sort())]
    } catch (error) {
      console.error("Fehler beim Laden der Übungs-Tags:", error)
      this.$toast.error("Filter-Tags konnten nicht geladen werden.")
    }
  },
  methods: {
    selectExercise(exercise) {
      console.log(exercise._id)
      if (this.mode === "swap") {
        this.$emit("swap-selected", {
          newExercise: exercise,
          exerciseIndex: this.exerciseToSwap.originalIndex,
        })
      } else {
        this.$emit("add-selected", {
          newExercise: exercise,
          atIndex: this.insertionIndex,
        })
      }
      this.$emit("update:dialog", false)
    },
    getMuscleGroups(exercise) {
      if (!exercise || !exercise.tags) return []
      return exercise.tags.map((t) => t.name).slice(0, 4)
    },
    getImageForExercise(exercise) {
      if (exercise.picture) return `/img/uebungen/${exercise.picture.replace(/\.[^/.]+$/, "")}.webp`
      if (exercise.manual && exercise.manual[0]?.picture) {
        return `/img/uebungen/${exercise.manual[0].picture.replace(/\.[^/.]+$/, "")}.webp`
      }
      return this.getPlaceholderImage()
    },
    getPlaceholderImage() {
      return "/img/einmalig/imageUploadPlaceholder.jpg"
    },
  },
}
</script>

<style scoped>
.exercise-dialog-bg {
  background: #1e1e1e;
}

.list-container {
  overflow-y: auto;
}

.context-card {
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 8px 16px;
}
.context-label {
  font-size: 0.75rem;
  color: #a0a0a0;
  margin-bottom: -4px;
}

/* Styles copied from WorkoutSplitEdit.vue */
.glass-card {
  background: rgba(60, 60, 60, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.5), 0 1px 3px rgba(0, 0, 0, 0.2) !important;
  border-radius: 16px !important;
  transition: all 0.3s ease;
}

.exercise-card-source {
  border-radius: 12px !important;
  min-height: 92px;
  position: relative;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}
.exercise-card-source:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.6) !important;
}

.exercise-content {
  flex-grow: 1;
  padding: 0 16px;
  min-width: 0;
}

.muscle-chip {
  backdrop-filter: blur(5px);
  font-size: 0.65rem !important;
  padding: 0 6px;
}

.animated-item {
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.5s ease forwards;
  animation-delay: var(--animation-delay);
}

@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
